import { useState, useEffect } from "react"
import { Activity, Users2, Save, AlertCircle } from "lucide-react"
import { HRService } from "@/features/hr"
import type { LicensedAgentWithTeams, WeeklySchedule, WeeklyScheduleDay } from "@/features/hr"

interface LicensedAgentLoadBalancerProps {
  campaignName?: string
}

interface AgentConfig {
  onDuty: boolean
  maxQueue: number | null
  schedule: WeeklySchedule
}

const DAYS: { key: keyof WeeklySchedule; label: string }[] = [
  { key: 'monday', label: 'Mon' },
  { key: 'tuesday', label: 'Tue' },
  { key: 'wednesday', label: 'Wed' },
  { key: 'thursday', label: 'Thu' },
  { key: 'friday', label: 'Fri' },
  { key: 'saturday', label: 'Sat' },
  { key: 'sunday', label: 'Sun' },
]

const emptyDay: WeeklyScheduleDay = { enabled: false, start: '09:00', end: '17:00' }

const toConfig = (agent: LicensedAgentWithTeams): AgentConfig => ({
  onDuty: agent.onDuty,
  maxQueue: agent.maxQueue,
  schedule: DAYS.reduce((acc, { key }) => {
    acc[key] = agent.schedule?.[key] ?? { ...emptyDay }
    return acc
  }, {} as WeeklySchedule),
})

export default function LicensedAgentLoadBalancer(_props: LicensedAgentLoadBalancerProps) {
  const [agents, setAgents] = useState<LicensedAgentWithTeams[]>([])
  const [configs, setConfigs] = useState<Record<string, AgentConfig>>({})
  const [originalConfigs, setOriginalConfigs] = useState<Record<string, AgentConfig>>({})
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)

  const [saving, setSaving] = useState<boolean>(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [saveSuccess, setSaveSuccess] = useState<boolean>(false)

  const fetchAgents = async () => {
    try {
      setLoading(true)
      setError(null)

      const result = await HRService.getLicensedAgentsWithTeams()

      setAgents(result)
      const nextConfigs = result.reduce((acc, agent) => {
        acc[agent.id] = toConfig(agent)
        return acc
      }, {} as Record<string, AgentConfig>)
      setConfigs(nextConfigs)
      setOriginalConfigs(nextConfigs)
    } catch (err) {
      console.error('Failed to fetch licensed agents:', err)
      setError(err instanceof Error ? err.message : 'Failed to fetch licensed agents')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchAgents()
  }, [])

  const updateOnDuty = (agentId: string, onDuty: boolean) => {
    setConfigs(prev => ({ ...prev, [agentId]: { ...prev[agentId], onDuty } }))
  }

  const updateMaxQueue = (agentId: string, maxQueue: number | null) => {
    setConfigs(prev => ({ ...prev, [agentId]: { ...prev[agentId], maxQueue } }))
  }

  const updateScheduleDay = (agentId: string, day: keyof WeeklySchedule, patch: Partial<WeeklyScheduleDay>) => {
    setConfigs(prev => ({
      ...prev,
      [agentId]: {
        ...prev[agentId],
        schedule: {
          ...prev[agentId].schedule,
          [day]: { ...(prev[agentId].schedule[day] ?? emptyDay), ...patch },
        },
      },
    }))
  }

  const saveAssignments = async () => {
    try {
      setSaving(true)
      setSaveError(null)
      setSaveSuccess(false)

      const dirtyAgentIds = Object.keys(configs).filter(
        agentId => JSON.stringify(configs[agentId]) !== JSON.stringify(originalConfigs[agentId])
      )

      await Promise.all(
        dirtyAgentIds.map(agentId =>
          HRService.updateAgent(agentId, { loadBalancerConfig: configs[agentId] })
        )
      )

      // Re-fetch so the page reflects exactly what was persisted, no manual reload needed
      await fetchAgents()

      setSaveSuccess(true)
      setTimeout(() => setSaveSuccess(false), 3000)
    } catch (err) {
      console.error('Failed to save load balance configuration:', err)
      setSaveError(err instanceof Error ? err.message : 'Failed to save load balance configuration')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="bg-white rounded-3xl shadow-xl border border-gray-200 overflow-hidden">

        {/* Header */}
        <div className="bg-gradient-to-r from-red-50 to-pink-50 p-8 border-b border-gray-200">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-red-600 rounded-xl flex items-center justify-center shadow-lg">
                <Activity className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">Licensed Agent Load Balance</h2>
                <p className="text-gray-600">Configure on-duty status, max queue, and weekly schedule for each licensed agent</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={saveAssignments}
                disabled={saving}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl font-semibold text-sm transition-all duration-200 whitespace-nowrap ${saving
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : saveSuccess
                      ? 'bg-green-100 text-green-700 border border-green-200'
                      : 'bg-blue-600 text-white hover:bg-blue-700 shadow-lg hover:shadow-xl'
                  }`}
              >
                {saving ? (
                  <>
                    <div className="w-4 h-4 bg-white/30 rounded animate-pulse"></div>
                    <span>Saving...</span>
                  </>
                ) : saveSuccess ? (
                  <>
                    <div className="w-4 h-4 bg-green-500 rounded-full"></div>
                    <span>Saved!</span>
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    <span>Save Changes</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Save Error Display */}
        {saveError && (
          <div className="mx-8 mb-4 mt-4 p-4 bg-red-50 border border-red-200 rounded-xl">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-red-500 rounded-full"></div>
              <span className="text-red-700 font-semibold">Save Error</span>
            </div>
            <p className="text-red-600 text-sm mt-1">{saveError}</p>
            <button
              onClick={() => setSaveError(null)}
              className="text-red-500 hover:text-red-700 text-xs mt-2"
            >
              Dismiss
            </button>
          </div>
        )}

        <div className="p-8">
          {loading ? (
            // Shimmer loading state
            <div className="overflow-x-auto">
              <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                <div className="divide-y divide-gray-200">
                  {[1, 2, 3, 4].map((index) => (
                    <div key={index} className="p-4">
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 bg-gray-200 rounded-lg animate-pulse"></div>
                        <div className="flex-1 space-y-2">
                          <div className="h-4 bg-gray-200 rounded animate-pulse w-1/3"></div>
                          <div className="h-3 bg-gray-200 rounded animate-pulse w-1/2"></div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : error ? (
            // Error state
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="text-red-600 mb-2">Failed to load licensed agents</div>
                <div className="text-sm text-gray-500">{error}</div>
                <button
                  onClick={() => window.location.reload()}
                  className="mt-3 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Retry
                </button>
              </div>
            </div>
          ) : agents.length === 0 ? (
            // Empty state
            <div className="flex items-center justify-center py-12">
              <div className="text-center text-gray-500">
                <div className="mb-2">No licensed agents available</div>
                <div className="text-sm">Create licensed agents to get started</div>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {agents.map((agent) => {
                const config = configs[agent.id]
                if (!config) return null
                const overThreshold = config.maxQueue !== null && agent.currentQueueCount >= config.maxQueue

                return (
                  <div key={agent.id} className="border border-gray-200 rounded-xl overflow-hidden">
                    <div className="bg-gray-50 p-4 flex flex-wrap items-center justify-between gap-4">
                      {/* Agent Info */}
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Users2 className="w-4 h-4 text-blue-600" />
                        </div>
                        <div className="min-w-0">
                          <div className="font-semibold text-gray-900 text-sm truncate" title={`${agent.firstName} ${agent.lastName}`}>
                            {agent.firstName} {agent.lastName}
                          </div>
                          <div className="text-xs text-gray-500 truncate">{agent.email}</div>
                        </div>
                      </div>

                      <div className="flex items-center gap-6 flex-wrap">
                        {/* On Duty toggle */}
                        <label className="flex items-center gap-2 cursor-pointer select-none">
                          <span className="text-xs font-semibold text-gray-700">On Duty</span>
                          <button
                            type="button"
                            onClick={() => updateOnDuty(agent.id, !config.onDuty)}
                            className={`relative w-10 h-5 rounded-full transition-colors ${config.onDuty ? 'bg-green-500' : 'bg-gray-300'}`}
                          >
                            <span
                              className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${config.onDuty ? 'translate-x-5' : ''}`}
                            />
                          </button>
                        </label>

                        {/* Max Queue */}
                        <label className="flex items-center gap-2">
                          <span className="text-xs font-semibold text-gray-700">Max Queue</span>
                          <input
                            type="number"
                            min={0}
                            value={config.maxQueue ?? ''}
                            onChange={(e) => updateMaxQueue(agent.id, e.target.value === '' ? null : Number(e.target.value))}
                            placeholder="Unlimited"
                            className="w-24 text-xs border border-gray-300 rounded px-2 py-1 bg-white"
                          />
                        </label>

                        {/* Current Queue */}
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-semibold text-gray-700">Current Queue</span>
                          <span
                            className={`text-xs font-semibold px-2 py-1 rounded-lg border ${overThreshold
                                ? 'bg-red-50 text-red-700 border-red-200'
                                : 'bg-gray-100 text-gray-700 border-gray-200'
                              }`}
                          >
                            {agent.currentQueueCount}{config.maxQueue !== null ? ` / ${config.maxQueue}` : ''}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Weekly Schedule */}
                    <div className="p-4">
                      <div className="text-xs font-semibold text-gray-700 mb-2">Weekly Availability</div>
                      <div className="flex flex-wrap gap-3">
                        {DAYS.map(({ key, label }) => {
                          const day = config.schedule[key] ?? emptyDay
                          return (
                            <div key={key} className="flex items-center gap-2 border border-gray-200 rounded-lg px-3 py-2">
                              <label className="flex items-center gap-1.5 cursor-pointer select-none">
                                <input
                                  type="checkbox"
                                  checked={day.enabled}
                                  onChange={(e) => updateScheduleDay(agent.id, key, { enabled: e.target.checked })}
                                />
                                <span className="text-xs font-semibold text-gray-700 w-8">{label}</span>
                              </label>
                              <input
                                type="time"
                                value={day.start}
                                disabled={!day.enabled}
                                onChange={(e) => updateScheduleDay(agent.id, key, { start: e.target.value })}
                                className="text-xs border border-gray-300 rounded px-1 py-0.5 bg-white disabled:bg-gray-100 disabled:text-gray-400"
                              />
                              <span className="text-xs text-gray-400">-</span>
                              <input
                                type="time"
                                value={day.end}
                                disabled={!day.enabled}
                                onChange={(e) => updateScheduleDay(agent.id, key, { end: e.target.value })}
                                className="text-xs border border-gray-300 rounded px-1 py-0.5 bg-white disabled:bg-gray-100 disabled:text-gray-400"
                              />
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  </div>
                )
              })}

              <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-xl">
                <AlertCircle className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-blue-700">
                  These settings are informational for now — on-duty status, max queue, and schedule are not yet enforced when an agent claims an application.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
